/**
 * SSOManager - Single Sign-On and authentication provider for OptimalAvailability
 * Supports SAML 2.0, OAuth 2.0, and Active Directory/LDAP integration
 */
class SSOManager {
  /**
   * Configure SAML 2.0 authentication
   * @param {Object} config - SAML configuration with idpUrl, certificate, etc.
   * @returns {Object} SAML configuration object
   */
  static configureSAML(config = {}) {
    return {
      protocol: 'SAML2.0',
      enabled: true,
      entityId: config.entityId || 'https://optimal.world/saml',
      ssoLoginUrl: config.ssoLoginUrl || '',
      ssoLogoutUrl: config.ssoLogoutUrl || '',
      certificate: config.certificate || '',
      signatureAlgorithm: 'http://www.w3.org/2001/04/xmldsig-more#rsa-sha256',
      digestAlgorithm: 'http://www.w3.org/2001/04/xmlenc#sha256',
      assertionEncryption: config.assertionEncryption !== false,
      nameIdFormat: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
      attributeMapping: {
        email: config.emailAttribute || 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
        firstName: config.firstNameAttribute || 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
        lastName: config.lastNameAttribute || 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
        groups: config.groupsAttribute || 'http://schemas.xmlsoap.org/claims/Group'
      },
      clockTolerance: 60 // seconds
    };
  }

  /**
   * Configure OAuth 2.0 authentication
   * @param {Object} config - OAuth2 configuration with clientId, authEndpoint, etc.
   * @returns {Object} OAuth2 configuration object
   */
  static configureOAuth2(config = {}) {
    return {
      protocol: 'OAuth2.0',
      enabled: true,
      clientId: config.clientId || '',
      clientSecret: config.clientSecret || '',
      authorizationEndpoint: config.authorizationEndpoint || '',
      tokenEndpoint: config.tokenEndpoint || '',
      userInfoEndpoint: config.userInfoEndpoint || '',
      grantType: 'authorization_code',
      scopes: config.scopes || ['openid', 'profile', 'email'],
      responseType: 'code',
      redirectUri: config.redirectUri || 'https://optimal.world/auth/callback',
      pkce: config.pkce !== false, // Proof Key for Public Clients
      state: true,
      nonce: true,
      tokenExpiry: config.tokenExpiry || 3600,
      refreshTokenExpiry: config.refreshTokenExpiry || 86400 * 30
    };
  }

  /**
   * Configure Active Directory / LDAP authentication
   * @param {Object} config - AD/LDAP configuration with domain, ldapUrl, baseDN, etc.
   * @returns {Object} AD/LDAP configuration object
   */
  static configureActiveDirectory(config = {}) {
    return {
      protocol: 'LDAP/AD',
      enabled: true,
      domain: config.domain || '',
      ldapUrl: config.ldapUrl || '',
      baseDN: config.baseDN || '',
      bindDN: config.bindDN || '',
      bindPassword: config.bindPassword || '',
      userSearchFilter: config.userSearchFilter || '(|(uid={0})(mail={0}))',
      groupSearchFilter: config.groupSearchFilter || '(member={0})',
      userObjectClass: config.userObjectClass || 'inetOrgPerson',
      groupObjectClass: config.groupObjectClass || 'groupOfNames',
      emailAttribute: config.emailAttribute || 'mail',
      nameAttribute: config.nameAttribute || 'displayName',
      groupAttribute: config.groupAttribute || 'memberOf',
      port: config.port || 389,
      useSSL: config.useSSL !== false,
      timeout: config.timeout || 5000,
      poolSize: config.poolSize || 5
    };
  }

  /**
   * Validate SSO token structure and return decoded claims
   * @param {string} token - JWT or SAML token string
   * @param {string} provider - 'oauth2', 'saml', 'ad'
   * @returns {Object} Decoded claims { sub, email, name, iat, exp, ... }
   */
  static validateSSOToken(token, provider = 'oauth2') {
    if (!token) {
      throw new Error('Token is required');
    }

    let claims = {};

    if (provider === 'oauth2' || provider === 'jwt') {
      // Parse JWT (simple parsing, real implementation would verify signature)
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format');
      }

      try {
        // Decode payload (base64url)
        const payload = JSON.parse(Buffer.from(parts[1], 'base64url').toString());
        claims = payload;
      } catch (e) {
        throw new Error('Failed to decode JWT payload');
      }

      // Validate expiry
      if (claims.exp && claims.exp < Math.floor(Date.now() / 1000)) {
        throw new Error('Token has expired');
      }
    } else if (provider === 'saml') {
      // SAML token parsing (simplified)
      claims = {
        format: 'SAML2.0',
        subject: this._extractSAMLAttribute(token, 'NameID'),
        email: this._extractSAMLAttribute(token, 'email'),
        attributes: this._parseSAMLAttributes(token)
      };
    } else if (provider === 'ad') {
      // AD token validation (would connect to LDAP)
      claims = {
        format: 'AD/LDAP',
        subject: token, // typically a DN or userPrincipalName
        authenticated: true
      };
    }

    return claims;
  }

  /**
   * Map SSO claims to internal user object
   * @param {Object} claims - Claims from SSO provider
   * @param {string} provider - 'oauth2', 'saml', 'ad'
   * @returns {Object} Internal user object { id, email, name, role, groups }
   */
  static mapSSOClaimsToUser(claims, provider = 'oauth2') {
    const user = {
      id: claims.sub || claims.subject || claims.uid || claims.dn || '',
      email: claims.email || claims.mail || '',
      firstName: claims.given_name || claims.givenName || '',
      lastName: claims.family_name || claims.surname || '',
      name: claims.name || `${claims.given_name || ''} ${claims.family_name || ''}`.trim(),
      provider: provider,
      providerUserId: claims.sub || claims.subject || '',
      isEmailVerified: claims.email_verified !== false,
      groups: claims.groups || claims.memberOf || [],
      role: this._determineRoleFromClaims(claims),
      metadata: {
        lastLogin: new Date().toISOString(),
        loginProvider: provider,
        claimsSource: provider
      }
    };

    return user;
  }

  /**
   * Get list of supported SSO providers
   * @returns {Array} List of supported provider objects
   */
  static getSSOProviders() {
    return [
      {
        id: 'oauth2',
        name: 'OAuth 2.0',
        description: 'Generic OAuth 2.0 provider',
        protocol: 'OAuth2.0',
        supported: true
      },
      {
        id: 'saml',
        name: 'SAML 2.0',
        description: 'SAML 2.0 Enterprise SSO',
        protocol: 'SAML2.0',
        supported: true
      },
      {
        id: 'ad',
        name: 'Active Directory / LDAP',
        description: 'Windows Active Directory or LDAP',
        protocol: 'LDAP/AD',
        supported: true
      },
      {
        id: 'google',
        name: 'Google Login',
        description: 'Google OAuth 2.0 (Google Workspace)',
        protocol: 'OAuth2.0',
        supported: true,
        preset: true
      },
      {
        id: 'microsoft',
        name: 'Microsoft Entra ID',
        description: 'Azure AD / Microsoft Entra ID',
        protocol: 'OAuth2.0',
        supported: true,
        preset: true
      },
      {
        id: 'okta',
        name: 'Okta',
        description: 'Okta Identity Management',
        protocol: 'SAML2.0',
        supported: true,
        preset: true
      }
    ];
  }

  // ========== PRIVATE HELPERS ==========

  /**
   * Extract attribute value from SAML assertion
   * @private
   */
  static _extractSAMLAttribute(samlToken, attributeName) {
    // Simplified SAML parsing
    const regex = new RegExp(`<${attributeName}[^>]*>([^<]*)<\/${attributeName}>`, 'i');
    const match = samlToken.match(regex);
    return match ? match[1] : '';
  }

  /**
   * Parse SAML attributes from assertion
   * @private
   */
  static _parseSAMLAttributes(samlToken) {
    const attributes = {};
    const attrRegex = /<Attribute Name="([^"]*)"[^>]*>\s*<AttributeValue>([^<]*)<\/AttributeValue>\s*<\/Attribute>/g;
    let match;
    while ((match = attrRegex.exec(samlToken)) !== null) {
      attributes[match[1]] = match[2];
    }
    return attributes;
  }

  /**
   * Determine user role from SSO claims
   * @private
   */
  static _determineRoleFromClaims(claims) {
    // Check for role claim
    if (claims.role) return claims.role;
    if (claims.roles && Array.isArray(claims.roles) && claims.roles.length > 0) {
      return claims.roles[0];
    }

    // Check groups for role hints
    const groups = claims.groups || claims.memberOf || [];
    if (Array.isArray(groups)) {
      if (groups.some(g => g.toLowerCase().includes('admin'))) return 'admin';
      if (groups.some(g => g.toLowerCase().includes('engineer'))) return 'engineer';
      if (groups.some(g => g.toLowerCase().includes('viewer'))) return 'viewer';
    }

    return 'viewer'; // Default role
  }
}

/**
 * EnterpriseManager - Enterprise project management engine for OptimalAvailability
 * Provides version control, user/permission management, audit trails, and enterprise reporting
 * Matching AWB 4.0's Enterprise Reports capabilities
 *
 * @module enterpriseManager
 * @requires none (in-memory storage)
 */

class EnterpriseManager {
  constructor() {
    /** @type {Map<string, Object>} Version storage: versionId -> version object */
    this.versions = new Map();

    /** @type {Map<string, Object>} User storage: userId -> user object */
    this.users = new Map();

    /** @type {Map<string, Object>} Project permissions: projectId -> { userId -> level } */
    this.projectPermissions = new Map();

    /** @type {Map<string, Object>} Folder permissions: folderId -> { userId -> level } */
    this.folderPermissions = new Map();

    /** @type {Array<Object>} Audit log entries */
    this.auditLog = [];

    /** @type {Map<string, Object>} Project checkouts: projectId -> { userId, timestamp, locked } */
    this.checkouts = new Map();

    /** @type {number} Counter for generating unique version IDs */
    this._versionCounter = 0;

    /** @type {number} Counter for generating unique user IDs */
    this._userCounter = 0;
  }

  // ==================== VERSION CONTROL ====================

  /**
   * Create a snapshot version of project data
   * @param {Object} projectData - Current project state
   * @param {string} label - Version label/name
   * @param {string} author - Author/creator user ID
   * @returns {Object} Version object { id, timestamp, label, author, hash, data }
   */
  createVersion(projectData, label, author) {
    const versionId = `v_${++this._versionCounter}_${Date.now()}`;
    const hash = this._generateHash(JSON.stringify(projectData));

    const version = {
      id: versionId,
      timestamp: new Date().toISOString(),
      label: label || `Version ${this._versionCounter}`,
      author: author,
      hash: hash,
      data: JSON.parse(JSON.stringify(projectData)) // Deep copy
    };

    this.versions.set(versionId, version);
    this._logAction(author, 'CREATE_VERSION', { versionId, label });

    return version;
  }

  /**
   * Get all version history
   * @returns {Array<Object>} Array of version objects sorted by timestamp descending
   */
  getVersionHistory() {
    return Array.from(this.versions.values())
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }

  /**
   * Retrieve a specific version by ID
   * @param {string} versionId - Version ID
   * @returns {Object|null} Version object or null if not found
   */
  getVersion(versionId) {
    return this.versions.get(versionId) || null;
  }

  /**
   * Compare two versions with deep diff
   * @param {string} v1Id - First version ID
   * @param {string} v2Id - Second version ID
   * @returns {Object} Diff object { added: [], removed: [], modified: [{ path, oldValue, newValue }] }
   */
  compareVersions(v1Id, v2Id) {
    const v1 = this.versions.get(v1Id);
    const v2 = this.versions.get(v2Id);

    if (!v1 || !v2) {
      throw new Error(`One or both versions not found: ${v1Id}, ${v2Id}`);
    }

    const diffs = this._deepDiff(v1.data, v2.data, '');

    return {
      from: v1Id,
      to: v2Id,
      fromLabel: v1.label,
      toLabel: v2.label,
      fromTimestamp: v1.timestamp,
      toTimestamp: v2.timestamp,
      ...diffs
    };
  }

  /**
   * Restore project data from a specific version
   * @param {string} versionId - Version ID to restore
   * @returns {Object} Project data from that version
   */
  restoreVersion(versionId) {
    const version = this.versions.get(versionId);
    if (!version) {
      throw new Error(`Version not found: ${versionId}`);
    }

    return JSON.parse(JSON.stringify(version.data)); // Deep copy
  }

  /**
   * Delete a version
   * @param {string} versionId - Version ID to delete
   * @returns {boolean} True if deleted, false if not found
   */
  deleteVersion(versionId) {
    const existed = this.versions.has(versionId);
    if (existed) {
      this.versions.delete(versionId);
      this._logAction(null, 'DELETE_VERSION', { versionId });
    }
    return existed;
  }

  /**
   * Deep diff helper - recursively compares two objects
   * @private
   * @param {*} obj1 - First object
   * @param {*} obj2 - Second object
   * @param {string} path - Current path in object hierarchy
   * @returns {Object} { added: [], removed: [], modified: [] }
   */
  _deepDiff(obj1, obj2, path) {
    const added = [];
    const removed = [];
    const modified = [];

    // Get all keys from both objects
    const allKeys = new Set([
      ...Object.keys(obj1 || {}),
      ...Object.keys(obj2 || {})
    ]);

    for (const key of allKeys) {
      const currentPath = path ? `${path}.${key}` : key;
      const val1 = obj1?.[key];
      const val2 = obj2?.[key];

      // Check if key only in obj1 (removed)
      if (!(key in (obj2 || {}))) {
        removed.push({
          path: currentPath,
          oldValue: val1,
          newValue: undefined
        });
        continue;
      }

      // Check if key only in obj2 (added)
      if (!(key in (obj1 || {}))) {
        added.push({
          path: currentPath,
          oldValue: undefined,
          newValue: val2
        });
        continue;
      }

      // Both have the key - check if values differ
      const isObj1Complex = val1 !== null && typeof val1 === 'object';
      const isObj2Complex = val2 !== null && typeof val2 === 'object';

      if (isObj1Complex && isObj2Complex) {
        // Both are objects, recurse
        const nested = this._deepDiff(val1, val2, currentPath);
        added.push(...nested.added);
        removed.push(...nested.removed);
        modified.push(...nested.modified);
      } else if (JSON.stringify(val1) !== JSON.stringify(val2)) {
        // Values differ
        modified.push({
          path: currentPath,
          oldValue: val1,
          newValue: val2
        });
      }
    }

    return { added, removed, modified };
  }

  // ==================== USER & PERMISSION MANAGEMENT ====================

  /**
   * Add a user to the enterprise
   * @param {Object} user - User object { id?, name, email, role: 'admin'|'engineer'|'viewer' }
   * @returns {Object} Created user object with id
   */
  addUser(user) {
    const userId = user.id || `u_${++this._userCounter}`;

    const newUser = {
      id: userId,
      name: user.name,
      email: user.email,
      role: user.role || 'engineer',
      created: new Date().toISOString()
    };

    this.users.set(userId, newUser);
    this._logAction(null, 'ADD_USER', { userId, role: newUser.role });

    return newUser;
  }

  /**
   * Remove a user from the enterprise
   * @param {string} userId - User ID to remove
   * @returns {boolean} True if removed, false if not found
   */
  removeUser(userId) {
    const existed = this.users.has(userId);
    if (existed) {
      this.users.delete(userId);
      // Clean up all permissions for this user
      this.projectPermissions.forEach(perms => delete perms[userId]);
      this.folderPermissions.forEach(perms => delete perms[userId]);
      this._logAction(null, 'REMOVE_USER', { userId });
    }
    return existed;
  }

  /**
   * Get all users
   * @returns {Array<Object>} Array of user objects
   */
  getUsers() {
    return Array.from(this.users.values());
  }

  /**
   * Set project permission for a user
   * @param {string} userId - User ID
   * @param {string} projectId - Project ID
   * @param {string} level - Permission level: 'owner'|'edit'|'view'|'none'
   * @returns {Object} Updated permission
   */
  setProjectPermission(userId, projectId, level) {
    if (!this.projectPermissions.has(projectId)) {
      this.projectPermissions.set(projectId, {});
    }

    const perms = this.projectPermissions.get(projectId);
    if (level === 'none') {
      delete perms[userId];
    } else {
      perms[userId] = level;
    }

    this._logAction(null, 'SET_PROJECT_PERMISSION', { userId, projectId, level });

    return { userId, projectId, level };
  }

  /**
   * Set folder permission for a user
   * @param {string} userId - User ID
   * @param {string} folderId - Folder ID
   * @param {string} level - Permission level: 'owner'|'edit'|'view'|'none'
   * @returns {Object} Updated permission
   */
  setFolderPermission(userId, folderId, level) {
    if (!this.folderPermissions.has(folderId)) {
      this.folderPermissions.set(folderId, {});
    }

    const perms = this.folderPermissions.get(folderId);
    if (level === 'none') {
      delete perms[userId];
    } else {
      perms[userId] = level;
    }

    this._logAction(null, 'SET_FOLDER_PERMISSION', { userId, folderId, level });

    return { userId, folderId, level };
  }

  /**
   * Get all permissions for a project
   * @param {string} projectId - Project ID
   * @returns {Object} { userId -> level }
   */
  getProjectPermissions(projectId) {
    return this.projectPermissions.get(projectId) || {};
  }

  /**
   * Get all permissions for a folder
   * @param {string} folderId - Folder ID
   * @returns {Object} { userId -> level }
   */
  getFolderPermissions(folderId) {
    return this.folderPermissions.get(folderId) || {};
  }

  /**
   * Check if user has permission for a resource action
   * @param {string} userId - User ID
   * @param {string} resource - Resource identifier (projectId or folderId)
   * @param {string} action - Action: 'view'|'edit'|'owner'
   * @returns {boolean} True if user has permission
   */
  checkPermission(userId, resource, action) {
    const user = this.users.get(userId);
    if (!user) return false;

    // Admins have all permissions
    if (user.role === 'admin') return true;

    // Check project permissions first
    const projectPerms = this.projectPermissions.get(resource);
    if (projectPerms && projectPerms[userId]) {
      const level = projectPerms[userId];
      if (level === 'owner') return true;
      if (action === 'edit' && level === 'edit') return true;
      if (action === 'view' && (level === 'view' || level === 'edit')) return true;
    }

    // Check folder permissions
    const folderPerms = this.folderPermissions.get(resource);
    if (folderPerms && folderPerms[userId]) {
      const level = folderPerms[userId];
      if (level === 'owner') return true;
      if (action === 'edit' && level === 'edit') return true;
      if (action === 'view' && (level === 'view' || level === 'edit')) return true;
    }

    return false;
  }

  /**
   * Get all permissions for a user across all resources
   * @param {string} userId - User ID
   * @returns {Object} { projects: {}, folders: {} }
   */
  getUserPermissions(userId) {
    const projects = {};
    const folders = {};

    this.projectPermissions.forEach((perms, projectId) => {
      if (userId in perms) {
        projects[projectId] = perms[userId];
      }
    });

    this.folderPermissions.forEach((perms, folderId) => {
      if (userId in perms) {
        folders[folderId] = perms[userId];
      }
    });

    return { projects, folders };
  }

  // ==================== AUDIT TRAIL ====================

  /**
   * Log an action to the audit trail
   * @param {string} userId - User ID performing action (or null for system)
   * @param {string} action - Action type (e.g., 'CREATE_VERSION', 'EDIT_PROJECT')
   * @param {Object} details - Additional details about the action
   * @returns {Object} Audit log entry
   */
  logAction(userId, action, details) {
    return this._logAction(userId, action, details);
  }

  /**
   * Internal action logger
   * @private
   * @param {string} userId - User ID
   * @param {string} action - Action type
   * @param {Object} details - Details
   * @returns {Object} Audit log entry
   */
  _logAction(userId, action, details) {
    const entry = {
      id: `log_${this.auditLog.length + 1}`,
      timestamp: new Date().toISOString(),
      userId: userId || 'system',
      action: action,
      details: details || {}
    };

    this.auditLog.push(entry);
    return entry;
  }

  /**
   * Get audit log entries with optional filtering
   * @param {Object} filters - Filter options { userId, action, dateFrom, dateTo, limit }
   * @returns {Array<Object>} Filtered audit log entries
   */
  getAuditLog(filters = {}) {
    let results = [...this.auditLog];

    if (filters.userId) {
      results = results.filter(e => e.userId === filters.userId);
    }

    if (filters.action) {
      results = results.filter(e => e.action === filters.action);
    }

    if (filters.dateFrom) {
      const fromTime = new Date(filters.dateFrom).getTime();
      results = results.filter(e => new Date(e.timestamp).getTime() >= fromTime);
    }

    if (filters.dateTo) {
      const toTime = new Date(filters.dateTo).getTime();
      results = results.filter(e => new Date(e.timestamp).getTime() <= toTime);
    }

    // Return in reverse chronological order
    results.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    if (filters.limit) {
      results = results.slice(0, filters.limit);
    }

    return results;
  }

  /**
   * Get checkout history
   * @returns {Array<Object>} Checkout history entries
   */
  getCheckoutHistory() {
    return Array.from(this.checkouts.values()).map(checkout => ({
      ...checkout,
      type: 'checkout'
    }));
  }

  /**
   * Checkout a project for exclusive editing
   * @param {string} userId - User ID checking out
   * @param {string} projectId - Project ID
   * @returns {Object} Checkout object { projectId, userId, timestamp, locked }
   */
  checkout(userId, projectId) {
    const existingCheckout = this.checkouts.get(projectId);
    if (existingCheckout && existingCheckout.locked) {
      throw new Error(`Project ${projectId} is already checked out by ${existingCheckout.userId}`);
    }

    const checkout = {
      projectId: projectId,
      userId: userId,
      timestamp: new Date().toISOString(),
      locked: true
    };

    this.checkouts.set(projectId, checkout);
    this._logAction(userId, 'CHECKOUT_PROJECT', { projectId });

    return checkout;
  }

  /**
   * Checkin a project, releasing the lock
   * @param {string} userId - User ID checking in
   * @param {string} projectId - Project ID
   * @param {string} message - Checkin message/notes
   * @returns {Object} Checkin result
   */
  checkin(userId, projectId, message) {
    const checkout = this.checkouts.get(projectId);

    if (!checkout) {
      throw new Error(`Project ${projectId} is not checked out`);
    }

    if (checkout.userId !== userId) {
      throw new Error(`Project checked out by ${checkout.userId}, not ${userId}`);
    }

    this.checkouts.delete(projectId);

    const result = {
      projectId: projectId,
      userId: userId,
      message: message,
      timestamp: new Date().toISOString()
    };

    this._logAction(userId, 'CHECKIN_PROJECT', { projectId, message });

    return result;
  }

  /**
   * Get checkout status for a project
   * @param {string} projectId - Project ID
   * @returns {Object|null} Checkout status { projectId, userId, timestamp, locked } or null
   */
  isCheckedOut(projectId) {
    return this.checkouts.get(projectId) || null;
  }

  // ==================== ENTERPRISE REPORTS ====================

  /**
   * Generate a summary report across multiple projects
   * @param {Array<Object>} projects - Array of project objects
   * @returns {Object} Project summary report
   */
  generateProjectSummaryReport(projects) {
    const report = {
      reportType: 'ProjectSummary',
      generated: new Date().toISOString(),
      totalProjects: projects.length,
      projects: projects.map(p => ({
        id: p.id,
        name: p.name,
        owner: p.owner,
        created: p.created,
        modified: p.modified,
        status: p.status || 'active'
      })),
      userStats: {
        totalUsers: this.users.size,
        byRole: this._getUserStats()
      },
      versionStats: {
        totalVersions: this.versions.size,
        byProject: this._getVersionStats()
      }
    };

    this._logAction(null, 'GENERATE_PROJECT_SUMMARY_REPORT', {});
    return report;
  }

  /**
   * Generate detailed report for a specific project
   * @param {Object} project - Project object
   * @returns {Object} Detailed project report
   */
  generateProjectDetailedReport(project) {
    const permissions = this.getProjectPermissions(project.id);
    const checkout = this.isCheckedOut(project.id);
    const relatedAudit = this.getAuditLog({ action: 'EDIT_PROJECT' }).filter(
      e => e.details.projectId === project.id
    );

    const report = {
      reportType: 'ProjectDetailed',
      generated: new Date().toISOString(),
      project: {
        id: project.id,
        name: project.name,
        description: project.description,
        owner: project.owner,
        created: project.created,
        modified: project.modified,
        status: project.status
      },
      permissions: permissions,
      usersWithAccess: Object.keys(permissions).length,
      currentCheckout: checkout,
      recentActivity: relatedAudit.slice(0, 20),
      versionCount: Array.from(this.versions.values()).filter(
        v => v.data.projectId === project.id
      ).length
    };

    this._logAction(null, 'GENERATE_PROJECT_DETAILED_REPORT', { projectId: project.id });
    return report;
  }

  /**
   * Generate version comparison report
   * @param {string} v1Id - First version ID
   * @param {string} v2Id - Second version ID
   * @returns {Object} Version diff report with formatting
   */
  generateVersionDiffReport(v1Id, v2Id) {
    const comparison = this.compareVersions(v1Id, v2Id);

    const report = {
      reportType: 'VersionDiff',
      generated: new Date().toISOString(),
      comparison: comparison,
      summary: {
        addedCount: comparison.added.length,
        removedCount: comparison.removed.length,
        modifiedCount: comparison.modified.length,
        totalChanges: comparison.added.length + comparison.removed.length + comparison.modified.length
      }
    };

    this._logAction(null, 'GENERATE_VERSION_DIFF_REPORT', { v1Id, v2Id });
    return report;
  }

  /**
   * Generate user permissions report
   * @returns {Object} All user permissions across projects/folders
   */
  generateUserPermissionsReport() {
    const report = {
      reportType: 'UserPermissions',
      generated: new Date().toISOString(),
      users: [],
      totalUsers: this.users.size,
      totalPermissions: this.projectPermissions.size + this.folderPermissions.size
    };

    this.users.forEach(user => {
      const perms = this.getUserPermissions(user.id);
      report.users.push({
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        projectPermissions: Object.keys(perms.projects).length,
        folderPermissions: Object.keys(perms.folders).length,
        permissions: perms
      });
    });

    this._logAction(null, 'GENERATE_USER_PERMISSIONS_REPORT', {});
    return report;
  }

  /**
   * Generate project checkout report
   * @returns {Object} Checkout history and current status
   */
  generateCheckoutReport() {
    const report = {
      reportType: 'CheckoutHistory',
      generated: new Date().toISOString(),
      currentCheckouts: Array.from(this.checkouts.values()),
      checkoutHistory: this.getAuditLog({ action: 'CHECKOUT_PROJECT' }),
      checkinHistory: this.getAuditLog({ action: 'CHECKIN_PROJECT' })
    };

    this._logAction(null, 'GENERATE_CHECKOUT_REPORT', {});
    return report;
  }

  /**
   * Generate version creation report showing who created which versions
   * @returns {Object} Version creation history
   */
  generateVersionCreationReport() {
    const versionAudit = this.getAuditLog({ action: 'CREATE_VERSION' });

    const creationsByUser = {};
    versionAudit.forEach(entry => {
      if (!creationsByUser[entry.userId]) {
        creationsByUser[entry.userId] = [];
      }
      creationsByUser[entry.userId].push({
        timestamp: entry.timestamp,
        label: entry.details.label,
        versionId: entry.details.versionId
      });
    });

    const report = {
      reportType: 'VersionCreation',
      generated: new Date().toISOString(),
      totalVersions: this.versions.size,
      creationsByUser: creationsByUser,
      versions: this.getVersionHistory().map(v => ({
        id: v.id,
        label: v.label,
        author: v.author,
        timestamp: v.timestamp,
        hash: v.hash
      }))
    };

    this._logAction(null, 'GENERATE_VERSION_CREATION_REPORT', {});
    return report;
  }

  /**
   * Generate Differences report (Modification/Version/Table variants)
   * @param {string} sortBy - Sort variant: 'modification' | 'version' | 'table'
   * @returns {Object} Differences report
   */
  generateDifferencesReport(sortBy = 'modification') {
    const versionList = this.getVersionHistory();
    const diffs = [];

    for (let i = 1; i < versionList.length; i++) {
      const comparison = this.compareVersions(versionList[i].id, versionList[i - 1].id);
      diffs.push({
        version: versionList[i].label,
        previousVersion: versionList[i - 1].label,
        changes: {
          added: comparison.added.length,
          removed: comparison.removed.length,
          modified: comparison.modified.length
        },
        details: comparison
      });
    }

    // Sort based on variant
    if (sortBy === 'modification') {
      diffs.sort((a, b) => (b.changes.modified + b.changes.added) - (a.changes.modified + a.changes.added));
    } else if (sortBy === 'version') {
      diffs.sort((a, b) => new Date(b.details.toTimestamp) - new Date(a.details.fromTimestamp));
    }

    const report = {
      reportType: 'Differences',
      generated: new Date().toISOString(),
      sortBy: sortBy,
      totalDifferences: diffs.length,
      differences: diffs
    };

    this._logAction(null, 'GENERATE_DIFFERENCES_REPORT', { sortBy });
    return report;
  }

  /**
   * Generate Project Versions report
   * @returns {Object} Project versions report
   */
  generateProjectVersionsReport() {
    const versionsByProject = {};

    this.versions.forEach(version => {
      const projectId = version.data.projectId || 'unknown';
      if (!versionsByProject[projectId]) {
        versionsByProject[projectId] = [];
      }
      versionsByProject[projectId].push({
        id: version.id,
        label: version.label,
        author: version.author,
        timestamp: version.timestamp,
        hash: version.hash
      });
    });

    const report = {
      reportType: 'ProjectVersions',
      generated: new Date().toISOString(),
      projectCount: Object.keys(versionsByProject).length,
      versionsByProject: versionsByProject
    };

    this._logAction(null, 'GENERATE_PROJECT_VERSIONS_REPORT', {});
    return report;
  }

  /**
   * Generate User Folder Permissions report
   * @returns {Object} User folder permissions report
   */
  generateUserFolderPermissionsReport() {
    const report = {
      reportType: 'UserFolderPermissions',
      generated: new Date().toISOString(),
      folderCount: this.folderPermissions.size,
      permissions: []
    };

    this.folderPermissions.forEach((perms, folderId) => {
      Object.entries(perms).forEach(([userId, level]) => {
        const user = this.users.get(userId);
        report.permissions.push({
          folderId,
          userId,
          userName: user?.name || 'Unknown',
          level
        });
      });
    });

    this._logAction(null, 'GENERATE_USER_FOLDER_PERMISSIONS_REPORT', {});
    return report;
  }

  /**
   * Generate User Project Permissions report
   * @returns {Object} User project permissions report
   */
  generateUserProjectPermissionsReport() {
    const report = {
      reportType: 'UserProjectPermissions',
      generated: new Date().toISOString(),
      projectCount: this.projectPermissions.size,
      permissions: []
    };

    this.projectPermissions.forEach((perms, projectId) => {
      Object.entries(perms).forEach(([userId, level]) => {
        const user = this.users.get(userId);
        report.permissions.push({
          projectId,
          userId,
          userName: user?.name || 'Unknown',
          level
        });
      });
    });

    this._logAction(null, 'GENERATE_USER_PROJECT_PERMISSIONS_REPORT', {});
    return report;
  }

  /**
   * Generate Users - Project and Folder Permissions combined report
   * @returns {Object} Combined permissions report
   */
  generateUsersProjectFolderPermissionsReport() {
    const userPermissions = {};

    this.users.forEach(user => {
      userPermissions[user.id] = {
        userId: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        projectPermissions: {},
        folderPermissions: {}
      };
    });

    this.projectPermissions.forEach((perms, projectId) => {
      Object.entries(perms).forEach(([userId, level]) => {
        if (userPermissions[userId]) {
          userPermissions[userId].projectPermissions[projectId] = level;
        }
      });
    });

    this.folderPermissions.forEach((perms, folderId) => {
      Object.entries(perms).forEach(([userId, level]) => {
        if (userPermissions[userId]) {
          userPermissions[userId].folderPermissions[folderId] = level;
        }
      });
    });

    const report = {
      reportType: 'UsersProjectFolderPermissions',
      generated: new Date().toISOString(),
      totalUsers: this.users.size,
      users: Object.values(userPermissions)
    };

    this._logAction(null, 'GENERATE_USERS_PROJECT_FOLDER_PERMISSIONS_REPORT', {});
    return report;
  }

  /**
   * Generate Users - Project Checkouts report
   * @returns {Object} User checkout report
   */
  generateUsersProjectCheckoutsReport() {
    const report = {
      reportType: 'UsersProjectCheckouts',
      generated: new Date().toISOString(),
      currentCheckouts: [],
      checkoutHistory: []
    };

    this.checkouts.forEach((checkout, projectId) => {
      const user = this.users.get(checkout.userId);
      report.currentCheckouts.push({
        projectId,
        userId: checkout.userId,
        userName: user?.name || 'Unknown',
        timestamp: checkout.timestamp,
        locked: checkout.locked
      });
    });

    const checkoutAudit = this.getAuditLog({ action: 'CHECKOUT_PROJECT' });
    checkoutAudit.forEach(entry => {
      const user = this.users.get(entry.userId);
      report.checkoutHistory.push({
        projectId: entry.details.projectId,
        userId: entry.userId,
        userName: user?.name || 'Unknown',
        action: 'CHECKOUT',
        timestamp: entry.timestamp
      });
    });

    const checkinAudit = this.getAuditLog({ action: 'CHECKIN_PROJECT' });
    checkinAudit.forEach(entry => {
      const user = this.users.get(entry.userId);
      report.checkoutHistory.push({
        projectId: entry.details.projectId,
        userId: entry.userId,
        userName: user?.name || 'Unknown',
        action: 'CHECKIN',
        timestamp: entry.timestamp,
        message: entry.details.message
      });
    });

    this._logAction(null, 'GENERATE_USERS_PROJECT_CHECKOUTS_REPORT', {});
    return report;
  }

  /**
   * Generate Users - Project Versions by Checkout Period report
   * @returns {Object} Versions created during checkout periods
   */
  generateUsersProjectVersionsByCheckoutReport() {
    const report = {
      reportType: 'UsersProjectVersionsByCheckout',
      generated: new Date().toISOString(),
      versionsByCheckout: []
    };

    const checkoutAudit = this.getAuditLog({ action: 'CHECKOUT_PROJECT' });
    const checkinAudit = this.getAuditLog({ action: 'CHECKIN_PROJECT' });

    const checkoutPeriods = new Map();

    checkoutAudit.forEach(entry => {
      const projectId = entry.details.projectId;
      const key = `${projectId}-${entry.userId}`;

      if (!checkoutPeriods.has(key)) {
        checkoutPeriods.set(key, []);
      }

      const periods = checkoutPeriods.get(key);
      periods.push({
        checkoutTime: new Date(entry.timestamp),
        checkinTime: null,
        userId: entry.userId,
        projectId
      });
    });

    checkinAudit.forEach(entry => {
      const projectId = entry.details.projectId;
      const key = `${projectId}-${entry.userId}`;

      if (checkoutPeriods.has(key)) {
        const periods = checkoutPeriods.get(key);
        const lastPeriod = periods[periods.length - 1];
        if (lastPeriod && !lastPeriod.checkinTime) {
          lastPeriod.checkinTime = new Date(entry.timestamp);
        }
      }
    });

    checkoutPeriods.forEach((periods, key) => {
      periods.forEach(period => {
        const versionsInPeriod = Array.from(this.versions.values()).filter(v => {
          const vTime = new Date(v.timestamp);
          return vTime >= period.checkoutTime && (!period.checkinTime || vTime <= period.checkinTime);
        });

        if (versionsInPeriod.length > 0) {
          const user = this.users.get(period.userId);
          report.versionsByCheckout.push({
            projectId: period.projectId,
            userId: period.userId,
            userName: user?.name || 'Unknown',
            checkoutStart: period.checkoutTime.toISOString(),
            checkinEnd: period.checkinTime ? period.checkinTime.toISOString() : null,
            versionsCreated: versionsInPeriod.length,
            versions: versionsInPeriod.map(v => ({
              id: v.id,
              label: v.label,
              timestamp: v.timestamp
            }))
          });
        }
      });
    });

    this._logAction(null, 'GENERATE_USERS_PROJECT_VERSIONS_BY_CHECKOUT_REPORT', {});
    return report;
  }

  /**
   * Generate Users - Project Versions Created report
   * @returns {Object} Versions created by each user
   */
  generateUsersProjectVersionsCreatedReport() {
    const versionAudit = this.getAuditLog({ action: 'CREATE_VERSION' });

    const versionsByUser = {};
    versionAudit.forEach(entry => {
      const userId = entry.userId;
      const user = this.users.get(userId);

      if (!versionsByUser[userId]) {
        versionsByUser[userId] = {
          userId,
          userName: user?.name || 'Unknown',
          email: user?.email || '',
          role: user?.role || 'unknown',
          versionsCreated: 0,
          versions: []
        };
      }

      const version = this.versions.get(entry.details.versionId);
      if (version) {
        versionsByUser[userId].versions.push({
          versionId: entry.details.versionId,
          label: entry.details.label,
          timestamp: entry.timestamp,
          projectId: version.data.projectId || 'unknown'
        });
        versionsByUser[userId].versionsCreated++;
      }
    });

    const report = {
      reportType: 'UsersProjectVersionsCreated',
      generated: new Date().toISOString(),
      totalUsers: Object.keys(versionsByUser).length,
      users: Object.values(versionsByUser)
    };

    this._logAction(null, 'GENERATE_USERS_PROJECT_VERSIONS_CREATED_REPORT', {});
    return report;
  }

  /**
   * Generate Template report (project templates and their usage)
   * @returns {Object} Template usage report
   */
  generateTemplateReport() {
    const report = {
      reportType: 'Template',
      generated: new Date().toISOString(),
      templates: [],
      templateVersions: this.getVersionHistory().filter(v => v.data.isTemplate === true)
    };

    this._logAction(null, 'GENERATE_TEMPLATE_REPORT', {});
    return report;
  }

  // ==================== HELPERS ====================

  /**
   * Generate hash of data for version integrity
   * @private
   * @param {string} data - Data to hash
   * @returns {string} Hash string
   */
  _generateHash(data) {
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return `hash_${Math.abs(hash).toString(16)}`;
  }

  /**
   * Get user statistics by role
   * @private
   * @returns {Object} User counts by role
   */
  _getUserStats() {
    const stats = { admin: 0, engineer: 0, viewer: 0 };
    this.users.forEach(user => {
      stats[user.role] = (stats[user.role] || 0) + 1;
    });
    return stats;
  }

  /**
   * Get version statistics
   * @private
   * @returns {Object} Version counts by project
   */
  _getVersionStats() {
    const stats = {};
    this.versions.forEach(version => {
      const projectId = version.data.projectId || 'unknown';
      stats[projectId] = (stats[projectId] || 0) + 1;
    });
    return stats;
  }
}



// ============================================================================
// EN 15341:2019 — MAINTENANCE KEY PERFORMANCE INDICATORS
// ============================================================================
// Implements the three indicator families defined in EN 15341:
//   E — Economic indicators (cost-based)
//   T — Technical indicators (performance/reliability)
//   O — Organisational indicators (workforce/process)
// Each indicator includes: id, name, formula, unit, direction (higher/lower=better),
// benchmark ranges (world-class / good / acceptable / poor) per standard guidance.
// ============================================================================

class EN15341KPIEngine {
  constructor() {
    this.indicators = this._buildIndicatorCatalogue();
  }

  // ── Indicator catalogue ──────────────────────────────────────────────────
  _buildIndicatorCatalogue() {
    return {
      // ── ECONOMIC (E) ─────────────────────────────────────────────────────
      E1:  { id: 'E1',  cat: 'Economic', name: 'Total Maintenance Cost / Replacement Asset Value', unit: '%', direction: 'lower', benchmarks: { worldClass: 2, good: 4, acceptable: 6, poor: 10 }, formula: (d) => d.replacementAssetValue > 0 ? (d.totalMaintenanceCost / d.replacementAssetValue) * 100 : null },
      E2:  { id: 'E2',  cat: 'Economic', name: 'Total Maintenance Cost / Total Output', unit: '$/unit', direction: 'lower', benchmarks: { worldClass: 0.5, good: 1.5, acceptable: 3, poor: 6 }, formula: (d) => d.totalOutput > 0 ? d.totalMaintenanceCost / d.totalOutput : null },
      E3:  { id: 'E3',  cat: 'Economic', name: 'Corrective Maintenance Cost / Total Maintenance Cost', unit: '%', direction: 'lower', benchmarks: { worldClass: 15, good: 30, acceptable: 50, poor: 70 }, formula: (d) => d.totalMaintenanceCost > 0 ? (d.correctiveMaintenanceCost / d.totalMaintenanceCost) * 100 : null },
      E4:  { id: 'E4',  cat: 'Economic', name: 'Preventive Maintenance Cost / Total Maintenance Cost', unit: '%', direction: 'higher', benchmarks: { worldClass: 60, good: 45, acceptable: 30, poor: 15 }, formula: (d) => d.totalMaintenanceCost > 0 ? (d.preventiveMaintenanceCost / d.totalMaintenanceCost) * 100 : null },
      E5:  { id: 'E5',  cat: 'Economic', name: 'Condition-Based Maintenance Cost / Total Maintenance Cost', unit: '%', direction: 'higher', benchmarks: { worldClass: 40, good: 25, acceptable: 15, poor: 5 }, formula: (d) => d.totalMaintenanceCost > 0 ? (d.conditionBasedCost / d.totalMaintenanceCost) * 100 : null },
      E6:  { id: 'E6',  cat: 'Economic', name: 'Maintenance Employee Cost / Total Maintenance Cost', unit: '%', direction: 'lower', benchmarks: { worldClass: 25, good: 35, acceptable: 45, poor: 60 }, formula: (d) => d.totalMaintenanceCost > 0 ? (d.maintenanceLaborCost / d.totalMaintenanceCost) * 100 : null },
      E7:  { id: 'E7',  cat: 'Economic', name: 'Contractor Cost / Total Maintenance Cost', unit: '%', direction: 'lower', benchmarks: { worldClass: 10, good: 20, acceptable: 35, poor: 50 }, formula: (d) => d.totalMaintenanceCost > 0 ? (d.contractorCost / d.totalMaintenanceCost) * 100 : null },
      E8:  { id: 'E8',  cat: 'Economic', name: 'Material & Spares Cost / Total Maintenance Cost', unit: '%', direction: 'lower', benchmarks: { worldClass: 20, good: 30, acceptable: 40, poor: 55 }, formula: (d) => d.totalMaintenanceCost > 0 ? (d.materialSparesCost / d.totalMaintenanceCost) * 100 : null },
      E9:  { id: 'E9',  cat: 'Economic', name: 'Maintenance Stores Value / Total Maintenance Cost', unit: '%', direction: 'lower', benchmarks: { worldClass: 15, good: 25, acceptable: 40, poor: 60 }, formula: (d) => d.totalMaintenanceCost > 0 ? (d.storesValue / d.totalMaintenanceCost) * 100 : null },
      E10: { id: 'E10', cat: 'Economic', name: 'Emergency/Urgent Maintenance Cost / Total Corrective Cost', unit: '%', direction: 'lower', benchmarks: { worldClass: 5, good: 15, acceptable: 30, poor: 50 }, formula: (d) => d.correctiveMaintenanceCost > 0 ? (d.emergencyMaintenanceCost / d.correctiveMaintenanceCost) * 100 : null },
      E11: { id: 'E11', cat: 'Economic', name: 'Total Maintenance Cost / Quantity Produced', unit: '$/unit', direction: 'lower', benchmarks: { worldClass: 0.3, good: 1, acceptable: 2.5, poor: 5 }, formula: (d) => d.quantityProduced > 0 ? d.totalMaintenanceCost / d.quantityProduced : null },
      E12: { id: 'E12', cat: 'Economic', name: 'Maintenance Stores Turnover', unit: 'turns/yr', direction: 'higher', benchmarks: { worldClass: 4, good: 2.5, acceptable: 1.5, poor: 0.5 }, formula: (d) => d.storesValue > 0 ? d.annualSpareConsumption / d.storesValue : null },

      // ── TECHNICAL (T) ────────────────────────────────────────────────────
      T1:  { id: 'T1',  cat: 'Technical', name: 'Availability (Operational)', unit: '%', direction: 'higher', benchmarks: { worldClass: 97, good: 93, acceptable: 88, poor: 80 }, formula: (d) => (d.operatingTime + d.standbyTime) > 0 ? ((d.operatingTime + d.standbyTime) / (d.operatingTime + d.standbyTime + d.downtime)) * 100 : null },
      T2:  { id: 'T2',  cat: 'Technical', name: 'Achieved Availability', unit: '%', direction: 'higher', benchmarks: { worldClass: 98, good: 95, acceptable: 90, poor: 82 }, formula: (d) => d.systemAvailability || null },
      T3:  { id: 'T3',  cat: 'Technical', name: 'Mean Time Between Failures (MTBF)', unit: 'hours', direction: 'higher', benchmarks: { worldClass: 5000, good: 2500, acceptable: 1000, poor: 400 }, formula: (d) => d.mtbf || null },
      T4:  { id: 'T4',  cat: 'Technical', name: 'Mean Time To Repair (MTTR)', unit: 'hours', direction: 'lower', benchmarks: { worldClass: 1, good: 3, acceptable: 6, poor: 12 }, formula: (d) => d.mttr || null },
      T5:  { id: 'T5',  cat: 'Technical', name: 'Mean Time To Failure (MTTF)', unit: 'hours', direction: 'higher', benchmarks: { worldClass: 8000, good: 4000, acceptable: 2000, poor: 800 }, formula: (d) => d.mttf || (d.mtbf ? d.mtbf * 0.95 : null) },
      T6:  { id: 'T6',  cat: 'Technical', name: 'Failure Rate', unit: 'per 10⁶h', direction: 'lower', benchmarks: { worldClass: 50, good: 200, acceptable: 500, poor: 1200 }, formula: (d) => d.mtbf > 0 ? (1e6 / d.mtbf) : null },
      T7:  { id: 'T7',  cat: 'Technical', name: 'Overall Equipment Effectiveness (OEE)', unit: '%', direction: 'higher', benchmarks: { worldClass: 85, good: 70, acceptable: 55, poor: 40 }, formula: (d) => { const a = (d.systemAvailability || 0) / 100; const p = (d.performanceRate || 100) / 100; const q = (d.qualityRate || 100) / 100; return a > 0 ? a * p * q * 100 : null; } },
      T8:  { id: 'T8',  cat: 'Technical', name: 'Planned Maintenance Percentage (PMP)', unit: '%', direction: 'higher', benchmarks: { worldClass: 85, good: 70, acceptable: 55, poor: 35 }, formula: (d) => (d.plannedMaintenanceHours + d.unplannedMaintenanceHours) > 0 ? (d.plannedMaintenanceHours / (d.plannedMaintenanceHours + d.unplannedMaintenanceHours)) * 100 : null },
      T9:  { id: 'T9',  cat: 'Technical', name: 'Schedule Compliance', unit: '%', direction: 'higher', benchmarks: { worldClass: 95, good: 85, acceptable: 70, poor: 55 }, formula: (d) => d.scheduledWorkOrders > 0 ? (d.completedOnScheduleWOs / d.scheduledWorkOrders) * 100 : null },
      T10: { id: 'T10', cat: 'Technical', name: 'Mean Time Between Maintenance (MTBM)', unit: 'hours', direction: 'higher', benchmarks: { worldClass: 3000, good: 1500, acceptable: 700, poor: 300 }, formula: (d) => d.totalMaintenanceActions > 0 ? d.operatingTime / d.totalMaintenanceActions : null },
      T11: { id: 'T11', cat: 'Technical', name: 'Maintenance Downtime Rate', unit: '%', direction: 'lower', benchmarks: { worldClass: 2, good: 5, acceptable: 10, poor: 20 }, formula: (d) => (d.operatingTime + d.downtime) > 0 ? (d.downtime / (d.operatingTime + d.downtime)) * 100 : null },
      T12: { id: 'T12', cat: 'Technical', name: 'Corrective Maintenance Ratio', unit: '%', direction: 'lower', benchmarks: { worldClass: 15, good: 30, acceptable: 50, poor: 70 }, formula: (d) => d.totalWorkOrders > 0 ? (d.correctiveWorkOrders / d.totalWorkOrders) * 100 : null },
      T13: { id: 'T13', cat: 'Technical', name: 'PM Work Order Compliance', unit: '%', direction: 'higher', benchmarks: { worldClass: 98, good: 90, acceptable: 80, poor: 65 }, formula: (d) => d.pmWorkOrdersDue > 0 ? (d.pmWorkOrdersCompleted / d.pmWorkOrdersDue) * 100 : null },
      T14: { id: 'T14', cat: 'Technical', name: 'Rework Rate', unit: '%', direction: 'lower', benchmarks: { worldClass: 1, good: 3, acceptable: 8, poor: 15 }, formula: (d) => d.totalWorkOrders > 0 ? (d.reworkWorkOrders / d.totalWorkOrders) * 100 : null },

      // ── ORGANISATIONAL (O) ───────────────────────────────────────────────
      O1:  { id: 'O1',  cat: 'Organisational', name: 'Maintenance Workforce Utilisation', unit: '%', direction: 'higher', benchmarks: { worldClass: 85, good: 70, acceptable: 55, poor: 40 }, formula: (d) => d.availableMaintenanceHours > 0 ? (d.directMaintenanceHours / d.availableMaintenanceHours) * 100 : null },
      O2:  { id: 'O2',  cat: 'Organisational', name: 'Maintenance Overtime Rate', unit: '%', direction: 'lower', benchmarks: { worldClass: 2, good: 5, acceptable: 10, poor: 20 }, formula: (d) => d.availableMaintenanceHours > 0 ? (d.overtimeHours / d.availableMaintenanceHours) * 100 : null },
      O3:  { id: 'O3',  cat: 'Organisational', name: 'Training Hours per Maintenance Employee', unit: 'hours/yr', direction: 'higher', benchmarks: { worldClass: 80, good: 50, acceptable: 30, poor: 10 }, formula: (d) => d.maintenanceHeadcount > 0 ? d.trainingHours / d.maintenanceHeadcount : null },
      O4:  { id: 'O4',  cat: 'Organisational', name: 'Maintenance Staff per Production Unit', unit: 'ratio', direction: 'lower', benchmarks: { worldClass: 0.01, good: 0.03, acceptable: 0.06, poor: 0.1 }, formula: (d) => d.totalOutput > 0 ? d.maintenanceHeadcount / d.totalOutput : null },
      O5:  { id: 'O5',  cat: 'Organisational', name: 'Backlog in Weeks of Work', unit: 'weeks', direction: 'lower', benchmarks: { worldClass: 2, good: 4, acceptable: 6, poor: 10 }, formula: (d) => d.weeklyMaintenanceCapacity > 0 ? d.backlogHours / d.weeklyMaintenanceCapacity : null },
      O6:  { id: 'O6',  cat: 'Organisational', name: 'Work Order Planning Rate', unit: '%', direction: 'higher', benchmarks: { worldClass: 95, good: 85, acceptable: 70, poor: 50 }, formula: (d) => d.totalWorkOrders > 0 ? (d.plannedWorkOrders / d.totalWorkOrders) * 100 : null },
      O7:  { id: 'O7',  cat: 'Organisational', name: 'Maintenance Employee Absentee Rate', unit: '%', direction: 'lower', benchmarks: { worldClass: 2, good: 4, acceptable: 7, poor: 12 }, formula: (d) => d.availableMaintenanceDays > 0 ? (d.absentDays / d.availableMaintenanceDays) * 100 : null },
      O8:  { id: 'O8',  cat: 'Organisational', name: 'Maintenance Employee Turnover Rate', unit: '%', direction: 'lower', benchmarks: { worldClass: 3, good: 8, acceptable: 15, poor: 25 }, formula: (d) => d.maintenanceHeadcount > 0 ? (d.employeeSeparations / d.maintenanceHeadcount) * 100 : null },
      O9:  { id: 'O9',  cat: 'Organisational', name: 'Multi-Skilled Workforce Ratio', unit: '%', direction: 'higher', benchmarks: { worldClass: 60, good: 40, acceptable: 25, poor: 10 }, formula: (d) => d.maintenanceHeadcount > 0 ? (d.multiSkilledWorkers / d.maintenanceHeadcount) * 100 : null },
      O10: { id: 'O10', cat: 'Organisational', name: 'HSE Incident Rate (Maintenance)', unit: 'per 200kh', direction: 'lower', benchmarks: { worldClass: 0.5, good: 2, acceptable: 5, poor: 10 }, formula: (d) => d.maintenanceExposureHours > 0 ? (d.hseIncidents / d.maintenanceExposureHours) * 200000 : null },
    };
  }

  // ── Calculate all KPIs from input data ───────────────────────────────────
  calculateAll(inputData) {
    const results = {};
    for (const [id, indicator] of Object.entries(this.indicators)) {
      try {
        const value = indicator.formula(inputData);
        const rating = this._rate(value, indicator);
        results[id] = {
          id,
          category: indicator.cat,
          name: indicator.name,
          value: value !== null && value !== undefined && !isNaN(value) ? Math.round(value * 100) / 100 : null,
          unit: indicator.unit,
          direction: indicator.direction,
          rating,
          benchmarks: indicator.benchmarks
        };
      } catch (e) {
        results[id] = { id, category: indicator.cat, name: indicator.name, value: null, unit: indicator.unit, direction: indicator.direction, rating: 'no-data', benchmarks: indicator.benchmarks };
      }
    }
    return results;
  }

  // ── Rate a single value against benchmarks ───────────────────────────────
  _rate(value, indicator) {
    if (value === null || value === undefined || isNaN(value)) return 'no-data';
    const b = indicator.benchmarks;
    if (indicator.direction === 'lower') {
      if (value <= b.worldClass) return 'world-class';
      if (value <= b.good)       return 'good';
      if (value <= b.acceptable) return 'acceptable';
      return 'poor';
    } else {
      if (value >= b.worldClass) return 'world-class';
      if (value >= b.good)       return 'good';
      if (value >= b.acceptable) return 'acceptable';
      return 'poor';
    }
  }

  // ── Get indicators by category ───────────────────────────────────────────
  getByCategory(category) {
    return Object.values(this.indicators).filter(i => i.cat === category);
  }

  // ── Category summary: count ratings, compute composite score 0-100 ──────
  categorySummary(results, category) {
    const items = Object.values(results).filter(r => r.category === category && r.value !== null);
    const counts = { 'world-class': 0, good: 0, acceptable: 0, poor: 0, 'no-data': 0 };
    items.forEach(i => counts[i.rating]++);
    const noData = Object.values(results).filter(r => r.category === category && r.value === null).length;
    counts['no-data'] = noData;
    const total = items.length;
    const score = total > 0 ? Math.round(((counts['world-class'] * 100 + counts.good * 70 + counts.acceptable * 40 + counts.poor * 10) / total)) : 0;
    return { category, total: total + noData, evaluated: total, counts, score };
  }

  // ── Overall maintenance maturity score ───────────────────────────────────
  overallScore(results) {
    const cats = ['Economic', 'Technical', 'Organisational'];
    const summaries = cats.map(c => this.categorySummary(results, c));
    const totalEvaluated = summaries.reduce((s, c) => s + c.evaluated, 0);
    const weightedScore = totalEvaluated > 0 ? Math.round(summaries.reduce((s, c) => s + c.score * c.evaluated, 0) / totalEvaluated) : 0;
    const maturity = weightedScore >= 85 ? 'World-Class' : weightedScore >= 65 ? 'Proactive' : weightedScore >= 45 ? 'Developing' : weightedScore >= 25 ? 'Reactive' : 'Nascent';
    return { score: weightedScore, maturity, summaries };
  }

  // ── Build default input data template from project/simulation data ───────
  static buildInputFromProject(projectData, simResults) {
    const equipments = projectData?.equipments || [];
    const failureModels = projectData?.failuremodels || [];
    const spares = projectData?.spares || [];

    // Derive what we can from simulation results
    const systemAvailability = simResults?.systemAvailability ? parseFloat(simResults.systemAvailability) : null;
    const mtbf = simResults?.mtbf ? parseFloat(simResults.mtbf) : null;
    const mttr = simResults?.mttr ? parseFloat(simResults.mttr) : null;
    const totalCost = simResults?.totalCost || 0;
    const costBreakdown = simResults?.costBreakdown || {};

    // Estimate operating/downtime from availability
    const annualHours = 8760;
    const availFrac = systemAvailability ? systemAvailability / 100 : 0.9;
    const operatingTime = annualHours * availFrac;
    const downtime = annualHours - operatingTime;

    // Spares value
    const storesValue = spares.reduce((s, sp) => s + (sp.unitCost || 0) * (sp.currentStock || sp.quantity || 0), 0);
    const annualSpareConsumption = spares.reduce((s, sp) => s + (sp.unitCost || 0) * (sp.annualDemand || sp.demandRate || 1), 0);

    return {
      // Economic inputs
      totalMaintenanceCost: totalCost || 500000,
      replacementAssetValue: equipments.length * 250000 || 5000000,
      correctiveMaintenanceCost: costBreakdown.labor || totalCost * 0.35,
      preventiveMaintenanceCost: totalCost * 0.40,
      conditionBasedCost: totalCost * 0.15,
      maintenanceLaborCost: costBreakdown.labor || totalCost * 0.35,
      contractorCost: totalCost * 0.12,
      materialSparesCost: costBreakdown.spares || totalCost * 0.30,
      storesValue: storesValue || totalCost * 0.25,
      emergencyMaintenanceCost: totalCost * 0.08,
      totalOutput: 100000,
      quantityProduced: 100000,
      annualSpareConsumption: annualSpareConsumption || totalCost * 0.28,

      // Technical inputs
      systemAvailability,
      mtbf,
      mttr,
      mttf: mtbf ? mtbf * 0.95 : null,
      operatingTime,
      standbyTime: 0,
      downtime,
      performanceRate: 95,
      qualityRate: 99,
      plannedMaintenanceHours: operatingTime * 0.06,
      unplannedMaintenanceHours: downtime * 0.6,
      scheduledWorkOrders: failureModels.length * 12 || 120,
      completedOnScheduleWOs: failureModels.length * 10 || 100,
      totalMaintenanceActions: failureModels.length * 15 || 150,
      totalWorkOrders: failureModels.length * 15 || 150,
      correctiveWorkOrders: failureModels.length * 5 || 50,
      pmWorkOrdersDue: failureModels.length * 12 || 120,
      pmWorkOrdersCompleted: failureModels.length * 11 || 110,
      reworkWorkOrders: Math.ceil(failureModels.length * 0.3) || 3,
      plannedWorkOrders: failureModels.length * 12 || 120,

      // Organisational inputs
      maintenanceHeadcount: Math.max(Math.ceil(equipments.length / 5), 4),
      availableMaintenanceHours: Math.max(Math.ceil(equipments.length / 5), 4) * 2080,
      directMaintenanceHours: Math.max(Math.ceil(equipments.length / 5), 4) * 2080 * 0.65,
      overtimeHours: Math.max(Math.ceil(equipments.length / 5), 4) * 2080 * 0.05,
      trainingHours: Math.max(Math.ceil(equipments.length / 5), 4) * 40,
      backlogHours: 400,
      weeklyMaintenanceCapacity: Math.max(Math.ceil(equipments.length / 5), 4) * 40,
      availableMaintenanceDays: Math.max(Math.ceil(equipments.length / 5), 4) * 260,
      absentDays: Math.max(Math.ceil(equipments.length / 5), 4) * 12,
      employeeSeparations: 1,
      multiSkilledWorkers: Math.ceil(Math.max(Math.ceil(equipments.length / 5), 4) * 0.4),
      maintenanceExposureHours: Math.max(Math.ceil(equipments.length / 5), 4) * 2080,
      hseIncidents: 1,
    };
  }
}

export default EnterpriseManager;
export { SSOManager, EN15341KPIEngine };

